// Copyright 2025, University of Maryland, All Rights Reserved
import React from 'react'
import type { SchemaFieldType } from "@/types/schema-fields"

// Field type configuration interface
export interface FieldTypeConfig {
  label: string
  description: string
  hasCustomConfig: boolean
  configComponent?: React.ComponentType<any>
  defaultValue?: any
}

// Centralized field type definitions
export const FIELD_TYPE_CONFIGS: Record<SchemaFieldType, FieldTypeConfig> = {
  string: {
    label: 'Text',
    description: 'Single line text input',
    hasCustomConfig: false,
    defaultValue: ''
  },
  integer: {
    label: 'Number', 
    description: 'Numeric input with min/max validation',
    hasCustomConfig: true,
    defaultValue: 0
  },
  simple_array: {
    label: 'Text List',
    description: 'List of text items',
    hasCustomConfig: false,
    defaultValue: []
  },
  enum_field: {
    label: 'Dropdown',
    description: 'Select from predefined options',
    hasCustomConfig: true,
    defaultValue: ''
  },
  score: {
    label: 'Score',
    description: 'Numeric score with type classification',
    hasCustomConfig: true,
    defaultValue: { score: 0, score_type: 'neutral' }
  },
  object_array: {
    label: 'Object List',
    description: 'List of structured objects',
    hasCustomConfig: true,
    defaultValue: []
  }
}

// Helper function to get field type config with proper defaults
export function getFieldTypeConfig(fieldType: SchemaFieldType): FieldTypeConfig {
  const base_config = FIELD_TYPE_CONFIGS[fieldType]
  
  // Return a config object with proper defaults for each field type
  switch (fieldType) {
    case 'score':
      return {
        ...base_config,
        type: 'score' as SchemaFieldType,
        title: base_config.label,
        description: base_config.description,
        score_min: 1,
        score_max: 10,
        type_options: ['positive', 'negative', 'neutral']
      } as any
    
    case 'integer':
      return {
        ...base_config,
        type: 'integer' as SchemaFieldType,
        title: base_config.label,
        description: base_config.description,
        min: 0,
        max: 100
      } as any
    
    case 'enum_field':
      return {
        ...base_config,
        type: 'enum_field' as SchemaFieldType,
        title: base_config.label,
        description: base_config.description,
        enum_options: []
      } as any
    
    case 'object_array':
      return {
        ...base_config,
        type: 'object_array' as SchemaFieldType,
        title: base_config.label,
        description: base_config.description,
        properties: {}
      } as any
    
    default:
      return {
        ...base_config,
        type: fieldType,
        title: base_config.label,
        description: base_config.description
      } as any
  }
}

// Helper function to check if a field type has custom configuration
export function hasCustomFieldConfig(fieldType: SchemaFieldType): boolean {
  return FIELD_TYPE_CONFIGS[fieldType].hasCustomConfig
}

// Get available field types for a schema type
export function getAvailableFieldTypesForSchema(): SchemaFieldType[] {
  // This could be expanded to filter based on schema type
  return Object.keys(FIELD_TYPE_CONFIGS) as SchemaFieldType[]
}
